import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { Modal, Button } from 'react-bootstrap'

interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'primary'
  icon?: string
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextType | null>(null)

export function useConfirm() {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return context.confirm
}

interface ConfirmProviderProps {
  children: ReactNode
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions>({
    message: ''
  })
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null)

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts)
    setIsOpen(true)

    return new Promise<boolean>((resolve) => {
      setResolveRef(() => resolve)
    })
  }, [])

  const handleConfirm = () => {
    setIsOpen(false)
    resolveRef?.(true)
  }

  const handleCancel = () => {
    setIsOpen(false)
    resolveRef?.(false)
  }

  const getIcon = () => {
    if (options.icon) return options.icon
    switch (options.variant) {
      case 'danger':
        return 'bi-exclamation-triangle-fill text-danger'
      case 'warning':
        return 'bi-exclamation-circle-fill text-warning'
      default:
        return 'bi-question-circle-fill text-primary'
    }
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      <Modal
        show={isOpen}
        onHide={handleCancel}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`bi ${getIcon()} me-2`}></i>
            {options.title || 'Bestätigung'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{options.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            {options.cancelText || 'Abbrechen'}
          </Button>
          <Button
            variant={options.variant || 'primary'}
            onClick={handleConfirm}
          >
            {options.confirmText || 'Bestätigen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </ConfirmContext.Provider>
  )
}
